/**
 * footsteps.js — locomotion.
 *
 * Footsteps are the sound the player hears more than any other, so the cheap trick —
 * one step buffer pitch-shifted per material — is exactly the thing that must not
 * happen here. Each material is built from a different *mechanism*:
 *
 *   concrete  a hard slap: bright transient, tiny room tail, no sustain
 *   metal     a struck plate: inharmonic partials ringing for 250 ms
 *   wood      a hollow knock: one strong low-mid resonance, fast decay
 *   dirt      granular: a dozen tiny particle transients, no click at all
 *   sand      granular but higher and longer, essentially a shaped hiss
 *   glass     grit crunch: many very short high grains over a dull base
 *   water     a splash: upward-sweeping burst plus droplets
 *
 * Their spectral centroids therefore land hundreds of Hz apart, and the test harness
 * checks exactly that.
 *
 * Every step also carries a boot/gear layer — a low scuff and a faint kit rattle —
 * which is what makes the player feel like a person carrying equipment.
 */

import {
  noiseSource, startNoise, noiseBurst, grains, shape, biquad, sweep, chain,
  partial, modal, rand, gain,
} from './dsp.js';

/** Boot leather + webbing. Common to every material, quiet, but the ear misses it. */
function gear(ac, dest, t, rng, lv) {
  noiseBurst(ac, dest, t + 0.006, rng, {
    type: 'pink', filter: 'bandpass', freq: rand(rng, 1400, 2400), Q: 0.9,
    level: lv * 0.12, attack: 0.004, decay: 0.05,
  });
  if (rng() < 0.6) {
    modal(ac, dest, t + rand(rng, 0.02, 0.07), rand(rng, 2600, 4200), [
      [1.0, lv * 0.05, 0.03],
      [1.73, lv * 0.03, 0.02],
    ], { spread: 0.15, rng });
  }
}

const MATERIALS = {
  concrete(ac, dest, t, rng, lv) {
    noiseBurst(ac, dest, t, rng, {
      filter: 'highpass', freq: rand(rng, 1800, 2900), Q: 0.7,
      level: lv * 0.9, attack: 0.0006, decay: 0.011,
    });
    noiseBurst(ac, dest, t, rng, {
      freq: rand(rng, 480, 720), Q: 1.5, level: lv * 0.6, attack: 0.001, decay: 0.038,
    });
    partial(ac, dest, t, rand(rng, 165, 215), lv * 0.45, 0.045, { glideTo: 120 });
    // Short room slap — a footstep on concrete is always heard with its reflection.
    noiseBurst(ac, dest, t + 0.016, rng, {
      filter: 'lowpass', freq: 2600, Q: 0.7, level: lv * 0.13, attack: 0.006, decay: 0.1,
    });
  },

  metal(ac, dest, t, rng, lv) {
    noiseBurst(ac, dest, t, rng, {
      filter: 'highpass', freq: rand(rng, 2800, 4200), Q: 0.7,
      level: lv * 0.75, attack: 0.0004, decay: 0.006,
    });
    const base = rand(rng, 380, 620);
    modal(ac, dest, t, base, [
      [1.00, lv * 0.42, 0.26],
      [1.79, lv * 0.3, 0.21],
      [2.94, lv * 0.22, 0.16],
      [4.36, lv * 0.14, 0.11],
      [6.11, lv * 0.08, 0.07],
    ], { spread: 0.1, rng });
    partial(ac, dest, t, rand(rng, 120, 165), lv * 0.35, 0.06, { glideTo: 90 });
  },

  wood(ac, dest, t, rng, lv) {
    noiseBurst(ac, dest, t, rng, {
      filter: 'highpass', freq: rand(rng, 1200, 1900), Q: 0.7,
      level: lv * 0.5, attack: 0.0006, decay: 0.007,
    });
    const base = rand(rng, 190, 280);
    modal(ac, dest, t, base, [
      [1.00, lv * 0.7, 0.1],
      [2.31, lv * 0.28, 0.06],
      [3.87, lv * 0.12, 0.035],
    ], { spread: 0.06, rng });
    noiseBurst(ac, dest, t, rng, {
      filter: 'lowpass', freq: 2200, Q: 0.9, level: lv * 0.4, attack: 0.0008, decay: 0.03,
    });
    // Board flex — a faint creak on some steps.
    if (rng() < 0.35) {
      const f = rand(rng, 420, 780);
      partial(ac, dest, t + 0.02, f, lv * 0.06, 0.12, { glideTo: f * rand(rng, 0.8, 1.25), attack: 0.02 });
    }
  },

  dirt(ac, dest, t, rng, lv) {
    // Pure granular. No transient click, no resonance — soil has neither.
    noiseBurst(ac, dest, t, rng, {
      filter: 'lowpass', freq: rand(rng, 550, 850), Q: 0.9,
      level: lv * 0.6, attack: 0.003, decay: 0.05,
    });
    grains(ac, dest, t, rng, {
      count: 14, window: 0.075, loFreq: 500, hiFreq: 3200, Q: 3.2,
      level: lv * 0.33, grainDecay: 0.013, decayShape: 1.6,
    });
    partial(ac, dest, t, rand(rng, 85, 120), lv * 0.28, 0.06, { attack: 0.005, glideTo: 65 });
  },

  sand(ac, dest, t, rng, lv) {
    // Higher, longer and finer than dirt: many more, much smaller particles.
    grains(ac, dest, t, rng, {
      count: 22, window: 0.1, loFreq: 2000, hiFreq: 8000, Q: 2.4,
      level: lv * 0.3, grainDecay: 0.008, decayShape: 1.2,
    });
    const src = noiseSource(ac, 'white');
    const bp = biquad(ac, 'bandpass', 3200, 0.7);
    sweep(bp.frequency, t, rand(rng, 3400, 4600), rand(rng, 1600, 2400), 0.12);
    const g = shape(ac, t, [[0, 0], [0.012, lv * 0.42], [0.05, lv * 0.2], [0.13, 1e-5]]);
    chain(src, bp, g, dest);
    startNoise(src, t, 0.18, rng);
    partial(ac, dest, t, rand(rng, 95, 130), lv * 0.14, 0.04, { attack: 0.006 });
  },

  glass(ac, dest, t, rng, lv) {
    // Boots on broken glass: a dull base plus a crowd of tiny bright crunches.
    noiseBurst(ac, dest, t, rng, {
      filter: 'lowpass', freq: 900, Q: 0.9, level: lv * 0.4, attack: 0.002, decay: 0.03,
    });
    grains(ac, dest, t, rng, {
      count: 16, window: 0.085, loFreq: 3000, hiFreq: 11000, Q: 6,
      level: lv * 0.3, grainDecay: 0.007, decayShape: 1.4,
    });
    for (let i = 0; i < 3; i++) {
      modal(ac, dest, t + rand(rng, 0.005, 0.09), rand(rng, 3200, 6500), [
        [1.0, lv * rand(rng, 0.05, 0.13), rand(rng, 0.03, 0.07)],
      ], { spread: 0.2, rng });
    }
  },

  water(ac, dest, t, rng, lv) {
    const src = noiseSource(ac, 'white');
    const bp = biquad(ac, 'bandpass', 900, 0.65);
    sweep(bp.frequency, t, rand(rng, 600, 900), rand(rng, 2400, 3600), 0.1);
    const g = shape(ac, t, [[0, 0], [0.008, lv * 0.8], [0.06, lv * 0.3], [0.19, 1e-5]]);
    chain(src, bp, g, dest);
    startNoise(src, t, 0.25, rng);
    partial(ac, dest, t, rand(rng, 110, 165), lv * 0.3, 0.07, { glideTo: 80, attack: 0.004 });
    for (let i = 0; i < 5; i++) {
      const f = rand(rng, 800, 2400);
      partial(ac, dest, t + rand(rng, 0.03, 0.22), f, lv * rand(rng, 0.05, 0.13), rand(rng, 0.012, 0.03), {
        glideTo: f * rand(rng, 1.5, 2.6), attack: 0.001,
      });
    }
  },
};

MATERIALS.flesh = MATERIALS.dirt;
MATERIALS.grass = MATERIALS.dirt;

export const STEP_MATERIALS = MATERIALS;

export function step(ac, dest, t, rng, material = 'concrete', { level = 1, boots = 1 } = {}) {
  const fn = MATERIALS[material] ?? MATERIALS.concrete;
  const lv = level * (1 + (rng() - 0.5) * 0.22);
  // Heel then toe, a few ms apart. A single impulse reads as a tap; two read as a
  // foot, and the tiny gap is most of why.
  fn(ac, dest, t, rng, lv);
  if (rng() < 0.75) fn(ac, dest, t + rand(rng, 0.018, 0.042), rng, lv * rand(rng, 0.2, 0.4));
  if (boots) gear(ac, dest, t, rng, lv * boots);
}

/* --------------------------------------------------------- gross locomotion --- */

export function jump(ac, dest, t, rng) {
  // Push-off scuff, kit rattle, and a short breath. No pitched content — a jump that
  // has a *note* in it always sounds like a cartoon.
  noiseBurst(ac, dest, t, rng, {
    type: 'pink', filter: 'bandpass', freq: rand(rng, 900, 1400), Q: 0.8,
    level: 0.55, attack: 0.008, decay: 0.09, sweepTo: rand(rng, 1800, 2600),
  });
  partial(ac, dest, t, rand(rng, 120, 170), 0.28, 0.05, { glideTo: 90, attack: 0.004 });
  gear(ac, dest, t + 0.01, rng, 2.2);
  // Exhale.
  noiseBurst(ac, dest, t + 0.02, rng, {
    filter: 'bandpass', freq: 700, Q: 1.1, level: 0.2, attack: 0.03, decay: 0.16,
  });
}

export function land(ac, dest, t, rng) {
  // Weight arriving: low thud first, hard slap on top, gear settling after.
  partial(ac, dest, t, rand(rng, 78, 110), 0.85, 0.13, { glideTo: 52, attack: 0.002 });
  noiseBurst(ac, dest, t, rng, {
    filter: 'lowpass', freq: rand(rng, 500, 800), Q: 1.0, level: 0.7, attack: 0.0015, decay: 0.07,
  });
  noiseBurst(ac, dest, t, rng, {
    filter: 'highpass', freq: rand(rng, 1800, 2800), Q: 0.7, level: 0.55, attack: 0.0005, decay: 0.014,
  });
  for (let i = 0; i < 4; i++) {
    modal(ac, dest, t + rand(rng, 0.01, 0.13), rand(rng, 2200, 4600), [
      [1.0, rand(rng, 0.04, 0.1), rand(rng, 0.02, 0.05)],
      [1.81, rand(rng, 0.02, 0.05), 0.02],
    ], { spread: 0.15, rng });
  }
  noiseBurst(ac, dest, t + 0.02, rng, {
    filter: 'lowpass', freq: 2400, Q: 0.7, level: 0.16, attack: 0.008, decay: 0.18,
  });
  // Grunt of impact.
  noiseBurst(ac, dest, t + 0.03, rng, {
    filter: 'bandpass', freq: 620, Q: 1.4, level: 0.18, attack: 0.02, decay: 0.14,
  });
}

export function slide(ac, dest, t, rng) {
  // A sustained fricative: broadband noise through a bandpass that opens then closes,
  // with amplitude wobble from the body loading and unloading against the ground.
  const dur = 0.95;
  const src = noiseSource(ac, 'white', rand(rng, 0.9, 1.1));
  const bp = biquad(ac, 'bandpass', 1200, 0.55);
  bp.frequency.setValueAtTime(rand(rng, 800, 1100), t);
  bp.frequency.exponentialRampToValueAtTime(rand(rng, 2200, 3000), t + 0.18);
  bp.frequency.exponentialRampToValueAtTime(rand(rng, 500, 750), t + dur);
  const g = shape(ac, t, [
    [0, 0], [0.05, 0.85], [0.2, 0.7], [0.5, 0.45], [0.75, 0.22], [dur, 1e-5],
  ]);
  // Slow tremolo: friction is not smooth.
  const lfo = ac.createOscillator();
  lfo.type = 'sine';
  lfo.frequency.value = rand(rng, 9, 15);
  const lfoAmt = gain(ac, 0.14);
  chain(lfo, lfoAmt, g.gain);
  lfo.start(t); lfo.stop(t + dur + 0.05);
  chain(src, bp, g, dest);
  startNoise(src, t, dur + 0.05, rng);

  // Initial scrape transient and a low rumble through the boots.
  noiseBurst(ac, dest, t, rng, {
    filter: 'highpass', freq: 2400, Q: 0.7, level: 0.45, attack: 0.001, decay: 0.02,
  });
  const rum = noiseSource(ac, 'brown');
  const rlp = biquad(ac, 'lowpass', 260, 1.1);
  const rg = shape(ac, t, [[0, 0], [0.04, 0.5], [0.4, 0.25], [dur * 0.9, 1e-5]]);
  chain(rum, rlp, rg, dest);
  startNoise(rum, t, dur, rng);
  gear(ac, dest, t + 0.05, rng, 1.6);
  gear(ac, dest, t + 0.4, rng, 1.0);
}

export function mantle(ac, dest, t, rng) {
  // Hands slap the ledge, body drags over it, boots find purchase.
  noiseBurst(ac, dest, t, rng, {
    filter: 'bandpass', freq: rand(rng, 900, 1500), Q: 0.9, level: 0.6, attack: 0.0008, decay: 0.03,
  });
  const src = noiseSource(ac, 'pink');
  const bp = biquad(ac, 'bandpass', 1400, 0.7);
  sweep(bp.frequency, t + 0.02, 1100, 2600, 0.3);
  const g = shape(ac, t, [[0, 0], [0.05, 0.5], [0.2, 0.38], [0.36, 1e-5]]);
  chain(src, bp, g, dest);
  startNoise(src, t, 0.4, rng);
  gear(ac, dest, t + 0.06, rng, 2.0);
  gear(ac, dest, t + 0.24, rng, 1.4);
  // Effort exhale.
  noiseBurst(ac, dest, t + 0.1, rng, {
    filter: 'bandpass', freq: 560, Q: 1.3, level: 0.22, attack: 0.05, decay: 0.22,
  });
  partial(ac, dest, t + 0.34, rand(rng, 150, 200), 0.3, 0.05, { glideTo: 110 });
}
